import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { FullScreenTile } from './FullScreenTile.js';
import { MoreBelow } from './MoreBelow.js';
import { tileKey } from './track-references.js';
import { fitTiles, gridBox, tilePositions, tilesBelow } from './fit-tiles.js';
import { scrollDownBy } from './scroll-rows.js';
import { useElementSize } from '../../hooks/useElementSize.js';

/** Space between tiles, in pixels; the tile rule's expected values assume 12. */
export const FULL_SCREEN_TILE_GAP = 12;

interface TiledLayoutProps {
  /** Every tile, already in the stable order (`useCallTiles`) */
  tiles: TrackReferenceOrPlaceholder[];

  /** Subscribed microphone per participant identity, for the speaking ring */
  microphoneTrackRefs: ReadonlyMap<string, TrackReference>;

  /** Pin a tile by its `tileKey` (#124). Without it the tiles have no pin button. */
  onPin?: (key: string) => void;

  /** The `tileKey` whose pin button takes focus when it mounts ({@link FullScreenTile}) */
  focusRequestRef?: React.MutableRefObject<string | null>;
}

/**
 * The full-screen layout with nothing pinned: every participant in one grid,
 * sized by the tile rule of epic D8 ({@link fitTiles}).
 *
 * The area is measured with a `ResizeObserver`, so the tiles re-fit when the
 * window resizes and when the chat drawer opens or closes beside the overlay,
 * without a reload. Up to twelve tiles fill the area; from thirteen the tiles
 * keep the twelve-tile size and the extra rows scroll vertically, with a fade
 * and a "N more below" chip ({@link MoreBelow}).
 *
 * Tiles are the same `ParticipantVideoTile` as the corner tiles, so the
 * names, avatars, mute badges and speaking ring are identical, plus a pin
 * button ({@link FullScreenTile}) that switches to the pinned layout. Their size is
 * the frame's own `--video-tile-width` / `--video-tile-height`, overridden
 * here on the grid box, and each tile is placed absolutely from
 * {@link tilePositions}: a flat keyed list, so a re-fit moves tiles without
 * remounting their video, and the last row is centred.
 *
 * All tiles stay mounted while scrolled out of view. `adaptiveStream` already
 * pauses a subscribed video whose element is not visible and sizes the rest
 * to their element, so the epic's "measure before unmounting" (§6.7) applies:
 * unmounting far rows is a later optimisation if a real 25-person call shows
 * it is needed.
 */
const TiledLayoutComponent: React.FC<TiledLayoutProps> = ({
  tiles,
  microphoneTrackRefs,
  onPin,
  focusRequestRef
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const area = useElementSize(scrollRef);
  const count = tiles.length;

  const fit = useMemo(
    () => fitTiles(count, area.width, area.height, FULL_SCREEN_TILE_GAP),
    [count, area.width, area.height]
  );
  const box = gridBox(fit, FULL_SCREEN_TILE_GAP);
  const positions = useMemo(
    () => tilePositions(count, fit, FULL_SCREEN_TILE_GAP),
    [count, fit]
  );

  // Centred vertically while everything fits; from the top once it scrolls.
  const offsetTop = box.height < area.height ? Math.floor((area.height - box.height) / 2) : 0;

  const [moreBelow, setMoreBelow] = useState(0);

  const countMoreBelow = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    // Bottom of the visible window, in grid coordinates
    const visibleBottom = element.scrollTop + area.height - offsetTop;
    setMoreBelow(tilesBelow(count, fit, FULL_SCREEN_TILE_GAP, visibleBottom));
  }, [count, fit, area.height, offsetTop]);

  // Re-count on join, leave and re-fit, not only on scroll.
  useLayoutEffect(() => {
    countMoreBelow();
  }, [countMoreBelow]);

  const showNextRow = useCallback(() => {
    const element = scrollRef.current;
    if (element) scrollDownBy(element, fit.height + FULL_SCREEN_TILE_GAP);
  }, [fit.height]);

  const gridStyle = {
    width: box.width,
    height: box.height,
    marginTop: offsetTop,
    '--video-tile-width': `${fit.width}px`,
    '--video-tile-height': `${fit.height}px`
  } as React.CSSProperties;

  return (
    <div className="relative h-full w-full">
      <div
        ref={scrollRef}
        onScroll={countMoreBelow}
        data-testid="full-screen-tiles"
        // The padding leaves room for the speaking ring, which is drawn
        // outside the tile and would otherwise be clipped by the scroller.
        className="h-full w-full overflow-x-hidden overflow-y-auto p-4 md:p-6"
      >
        {fit.columns > 0 && (
          <div
            role="list"
            aria-label="Video call participants"
            className="relative mx-auto"
            style={gridStyle}
          >
            {tiles.map((trackRef, index) => (
              <div
                key={tileKey(trackRef)}
                role="listitem"
                className="absolute"
                style={{ left: positions[index]?.left ?? 0, top: positions[index]?.top ?? 0 }}
              >
                <FullScreenTile
                  trackRef={trackRef}
                  microphoneTrackRefs={microphoneTrackRefs}
                  onPin={onPin}
                  focusRequestRef={focusRequestRef}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <MoreBelow count={moreBelow} onShowMore={showNextRow} />
    </div>
  );
};

export const TiledLayout = memo(TiledLayoutComponent);
TiledLayout.displayName = 'TiledLayout';
